import * as React from "react";
import { useAuthFetch } from "../security/AckAuth";
import AckLogger from "../common/AckLogger";

/** Converts a response from the server to a JSON object. */
const convertResponse = async (response: Response) => {
  if (!response.ok) {
    throw new Error(
      `Error while communicating with server: [${response.statusText}]`
    );
  }
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch (err) {
    return text;
  }
};

/** Creates the headers for a request. */
/** Gets the value of a cookie by name. */
const getCookie = (name: string) => {
  if (!document.cookie) {
    return null;
  }
  const xsrfCookies = document.cookie
    .split(";")
    .map((c) => c.trim())
    .filter((c) => c.startsWith(name + "="));

  if (xsrfCookies.length === 0) {
    return null;
  }
  return decodeURIComponent(xsrfCookies[0].substring(name.length + 1));
};

/** Creates the headers for a request. */
const createHeaders = (): any => {
  const headers = { "Content-Type": "application/json" };
  const xsrfToken = getCookie("XSRF-TOKEN");
  if (xsrfToken) {
    // @ts-ignore
    headers["X-XSRF-TOKEN"] = xsrfToken;
  }
  return headers;
};

/** An interface for fetching data from the server. */
export interface FetchApi {
  /** Gets data from the server. */
  get(suffix: string): Promise<any>;

  /** Posts data to the server. */
  post(suffix: string, payload: object, raw?: boolean): Promise<any>;

  /** Patches data on the server. */
  patch(suffix: string, payload: object, raw?: boolean): Promise<any>;

  /** Deletes data from the server. */
  delete(suffix: string): Promise<any>;
}

/** A hook for fetching data from the server. */
export const useFetch = (path: string): FetchApi => {
  const authFetch = useAuthFetch();
  const options = (method: string, body: any = undefined) => {
    return {
      method: method,
      headers: createHeaders(),
      body: body,
    };
  };
  return {
    get: async (suffix: string) => {
      const url = `${path}/${suffix}`;
      AckLogger.info("GET", url);
      const response = await authFetch(url, options("GET"));
      return convertResponse(response);
    },

    post: async (suffix: string, payload: object, raw?: boolean) => {
      const url = `${path}/${suffix}`;
      const requestOptions = raw
        ? payload
        : options("POST", JSON.stringify(payload));
      AckLogger.info("POST", url, requestOptions);
      const response = await authFetch(url, requestOptions);
      return convertResponse(response);
    },

    patch: async (suffix: string, payload: object, raw?: boolean) => {
      const url = `${path}/${suffix}`;
      const requestOptions = raw
        ? payload
        : options("PATCH", JSON.stringify(payload));
      AckLogger.info("PATCH", url, requestOptions);
      const response = await authFetch(url, requestOptions);
      return convertResponse(response);
    },

    delete: async (suffix: string) => {
      const url = `${path}/${suffix}`;
      AckLogger.info("DELETE", url);
      const response = await authFetch(url, options("DELETE"));
      return convertResponse(response);
    },
  };
};
